
import { useEffect, useRef, useState } from 'react';

interface ScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export function useScrollAnimation(options: ScrollAnimationOptions = {}) {
  const { threshold = 0.15, rootMargin = '0px 0px -60px 0px', triggerOnce = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
}

export function getAnimationClass(isVisible: boolean, type: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in' = 'fade-in', delay = 0) {
  const baseTransition = `transition-all duration-700 ease-out`;
  const delayStyle = delay > 0 ? `delay-[${delay}ms]` : '';

  const hiddenStates: Record<string, string> = {
    'fade-in': 'opacity-0',
    'slide-up': 'opacity-0 translate-y-10',
    'slide-left': 'opacity-0 -translate-x-10',
    'slide-right': 'opacity-0 translate-x-10',
    'scale-in': 'opacity-0 scale-95',
  };

  const visibleState = 'opacity-100 translate-x-0 translate-y-0 scale-100';

  return `${baseTransition} ${delayStyle} ${isVisible ? visibleState : hiddenStates[type]}`;
}
